/**
 * Whether, and to what, a picked address sets the site's TDDA Region (S3.4, bug 5695).
 *
 * The geometry lives in Shared/Tools/KmlTerritories; this is the surrounding policy — the country
 * switches, the territory-to-region mapping, and what to do when any of it comes up empty. It is
 * separate from the form because getting it wrong is silent: the region feeds the Australian
 * confirmation-lab default, and the state resolver reads the region ahead of the address, so a region
 * set to the wrong franchise books the wrong laboratory with nothing on screen to show it.
 *
 * The rule throughout is that an undetected region is fine and a wrongly detected one is not. Every
 * uncertain path therefore clears the field and says so, rather than leaving whatever was there or
 * guessing at the nearest thing.
 */

import { loadTerritories, territoryAt } from "src/Shared/Tools/KmlTerritories";

export type TerritoryMapping = {
  /** The KML Placemark name, exactly as the map spells it. */
  territoryName: string;
  /** The CRM franchise id, already translated from the Imperans one by the API. */
  franchiseID: number;
};

export type AutoDetectSettings = {
  auEnabled: boolean;
  nzEnabled: boolean;
};

export type SiteRegionOutcome =
  /** Auto-detection does not apply; leave the field exactly as the user left it. */
  | { kind: "skip"; reason: "no-coordinates" | "disabled-for-country" | "not-configured" }
  /** Set the Region to this franchise. */
  | { kind: "detected"; franchiseID: number; territoryName: string }
  /** Clear the Region and tell the user to choose one. */
  | { kind: "undetected"; reason: "outside-all-territories" | "territory-not-mapped" };

export type SiteRegionInput = {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  /** The two-letter country of the picked address, as Google reports it. */
  countryCode: string | null | undefined;
  /** The territory the point falls in, or null; from `territoryAt`. */
  territoryName: string | null | undefined;
  mappings: readonly TerritoryMapping[] | null | undefined;
  settings: AutoDetectSettings | null | undefined;
};

/** Whether auto-detection is switched on for the country an address is in. */
export const isAutoDetectEnabled = (
  countryCode: string | null | undefined,
  settings: AutoDetectSettings | null | undefined,
): boolean => {
  if (!settings) return false;
  const code = `${countryCode ?? ""}`.trim().toUpperCase();
  if (code === "AU") return settings.auEnabled;
  if (code === "NZ") return settings.nzEnabled;
  // A country TDDA does not operate in has no territories to detect against.
  return false;
};

/**
 * Whether the coordinates are usable. Rejects the missing and the malformed, and also exactly
 * (0, 0) — a real point in the Atlantic, but in practice what a failed geocode looks like, and no
 * TDDA territory is anywhere near it.
 */
export const hasUsableCoordinates = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean => {
  if (latitude == null || longitude == null) return false;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return false;
  return !(latitude === 0 && longitude === 0);
};

export const decideSiteRegion = ({
  latitude,
  longitude,
  countryCode,
  territoryName,
  mappings,
  settings,
}: SiteRegionInput): SiteRegionOutcome => {
  // Nothing to go on. The address may have been typed rather than picked, and typing is always
  // allowed — so the field is left alone rather than cleared.
  if (!hasUsableCoordinates(latitude, longitude)) {
    return { kind: "skip", reason: "no-coordinates" };
  }

  if (!settings) return { kind: "skip", reason: "not-configured" };

  if (!isAutoDetectEnabled(countryCode, settings)) {
    return { kind: "skip", reason: "disabled-for-country" };
  }

  const name = `${territoryName ?? ""}`.trim();
  if (!name) return { kind: "undetected", reason: "outside-all-territories" };

  // Matched case-insensitively and trimmed: the name is administered in one database and drawn in a
  // file maintained by hand, and the two drifting by whitespace or capitalisation should not silently
  // stop a region resolving.
  const mapped = (mappings || []).find(
    (m) => `${m.territoryName ?? ""}`.trim().toLowerCase() === name.toLowerCase(),
  );

  if (!mapped || !Number.isFinite(mapped.franchiseID) || mapped.franchiseID <= 0) {
    return { kind: "undetected", reason: "territory-not-mapped" };
  }

  return { kind: "detected", franchiseID: mapped.franchiseID, territoryName: name };
};

/** What to tell the user when a region could not be detected. */
export const undetectedMessage = (
  reason: "outside-all-territories" | "territory-not-mapped",
): string =>
  reason === "outside-all-territories"
    ? "This address is not inside any mapped TDDA territory. Please select a Region."
    : "This address falls in a territory that is not assigned to a region yet. Please select a Region.";

export type SiteRegionSettings = AutoDetectSettings & { territories: readonly TerritoryMapping[] };

export type ResolvedAddress = {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  countryCode: string | null | undefined;
};

/**
 * The full detection pipeline behind `decideSiteRegion`: downloads the country's KML map only when
 * the coordinates and settings actually call for it, works out which territory the point falls in,
 * and returns the same outcome `decideSiteRegion` would.
 *
 * One implementation shared by the manual form (a picked autocomplete suggestion) and bulk import (a
 * geocoded CSV row, bug 5826), so both place a site in the same territory the same way — the two used
 * to disagree because bulk import never ran this pipeline at all, going straight to whatever exact
 * text the CSV's own Region column happened to spell.
 */
export const resolveSiteRegion = async (
  address: ResolvedAddress | null | undefined,
  settings: SiteRegionSettings | null | undefined,
): Promise<SiteRegionOutcome> => {
  if (!address) return { kind: "skip", reason: "no-coordinates" };

  const preflight = decideSiteRegion({
    latitude: address.latitude,
    longitude: address.longitude,
    countryCode: address.countryCode,
    territoryName: null,
    mappings: settings?.territories,
    settings,
  });

  // Only "undetected" needs the maps; a skip has already decided nothing should happen, and
  // downloading several megabytes to confirm that would be waste.
  if (preflight.kind === "skip") return preflight;

  const territories = await loadTerritories(address.countryCode);
  const territory = territoryAt(address.latitude!, address.longitude!, territories);

  return decideSiteRegion({
    latitude: address.latitude,
    longitude: address.longitude,
    countryCode: address.countryCode,
    territoryName: territory?.name,
    mappings: settings?.territories,
    settings,
  });
};
